import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, MousePointerClick } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdCard } from "@/components/AdCard";
import { AdFormDialog } from "@/components/business/AdFormDialog";

const STATUS = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-[#E3EFE6] text-[#0F2A1F] border-[#2F7D55]/30",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function BusinessDashboard() {
  const { user } = useAuth();
  const [ads, setAds] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/business/ads").then((r) => setAds(r.data));
  useEffect(() => { load(); }, []);
  const del = async (ad) => {
    try { await api.delete(`/business/ads/${ad.id}`); toast.success("Ad deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const total = (k) => (ads || []).reduce((n, a) => n + (a[k] || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-5 py-10 lg:py-14 space-y-8" data-testid="business-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#23603F]">Business account · free during pilot</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">{user?.profile?.business_name}</h1>
          <p className="mt-2 text-stone-600 max-w-xl">Reach people planning outings near you. Approved ads appear inside plans only when your deal is nearby and running during their outing.</p>
        </div>
        <button data-testid="business-new-ad-btn" onClick={() => setEditing({})} className="flex items-center gap-2 bg-[#2F7D55] hover:bg-[#23603F] text-white font-semibold px-5 py-3 rounded-xl transition-colors active:scale-95">
          <Plus className="w-4 h-4" /> New ad
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Ads", ads?.length], ["Live", ads?.filter((a) => a.status === "approved").length], ["Times shown", total("impressions")], ["Link clicks", total("clicks")]].map(([k, v]) => (
          <div key={k} className="rounded-2xl bg-white border border-stone-200 p-5" data-testid={`business-stat-${k.split(" ")[0].toLowerCase()}`}>
            <p className="text-4xl font-extrabold text-[#163A2B]" style={{ fontFamily: "Outfit" }}>{v ?? "–"}</p>
            <p className="text-xs uppercase tracking-wider text-stone-500 mt-1">{k}</p>
          </div>
        ))}
      </div>
      {ads?.length === 0 && (
        <div className="rounded-2xl border border-dashed border-stone-300 p-10 text-center" data-testid="business-empty">
          <p className="text-stone-600">No ads yet. Create one with your deal and the times it runs.</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {ads?.map((ad, i) => (
          <div key={ad.id} className="space-y-2 rise" data-testid={`business-ad-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <span data-testid={`business-ad-status-${i}`} className={`text-xs font-mono font-semibold px-2.5 py-1 rounded-full border ${STATUS[ad.status]}`}>
                {ad.status === "pending" ? "Waiting for review" : ad.status === "approved" ? "Approved · live" : "Not approved"}
              </span>
              <span className="flex items-center gap-3 text-xs text-stone-500 font-mono">
                <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {ad.impressions}</span>
                <span className="flex items-center gap-1"><MousePointerClick className="w-3.5 h-3.5" /> {ad.clicks}</span>
              </span>
            </div>
            {ad.status === "rejected" && ad.review_note && <p data-testid={`business-ad-note-${i}`} className="text-sm bg-red-50 text-red-800 rounded-xl px-3 py-2">Reviewer note: {ad.review_note}</p>}
            <AdCard ad={ad} testId={`business-ad-preview-${i}`}
              footer={<div className="flex gap-2 pt-2 border-t border-stone-100">
                <button data-testid={`business-ad-edit-${i}`} onClick={() => setEditing(ad)} className="flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg border hover:bg-stone-50"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button data-testid={`business-ad-delete-${i}`} onClick={() => del(ad)} className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border hover:bg-red-50 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>} />
          </div>
        ))}
      </div>
      {editing && <AdFormDialog open onOpenChange={(o) => !o && setEditing(null)} ad={editing.id ? editing : null} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
